import React from "react";
import moment from "moment";

import { getDepartmentColor, sortAbsencesForCalendar } from "./departmentColors";

const getPolishWeekdayHeaders = () =>
  Array.from({ length: 7 }, (_, index) =>
    moment()
      .locale("pl")
      .startOf("isoWeek")
      .add(index, "days")
      .format("dd")
  );

const VACATION_KINDS = ["vacation", "vacation_on_request"];
const SICK_LEAVE_KINDS = ["sick_leave", "sick_leave_not_planned"];

const getKindLabel = (kind) => {
  if (VACATION_KINDS.includes(kind)) {
    return "Urlop";
  }

  if (SICK_LEAVE_KINDS.includes(kind)) {
    return "Chor.";
  }

  return "";
};

const getCompanyName = (company_id) => {
  if (company_id === 1) {
    return "Tradercon";
  }

  if (company_id === 2) {
    return "Wisetrade";
  }

  return "";
};

const isWeekend = (day) => day.isoWeekday() >= 6;

const getAbsencesForDay = (day, absences) => {
  if (isWeekend(day)) {
    return [];
  }

  return sortAbsencesForCalendar(
    absences.filter((absence) =>
      moment(day).isBetween(absence.date_from, absence.date_to, "day", "[]")
    )
  );
};

const formatTileLabel = ({ first_name, last_name, kind }) => {
  const initial = (first_name || "").trim().charAt(0).toUpperCase();
  const surname = (last_name || "").trim();
  const kindLabel = getKindLabel(kind);
  const nameLabel = !surname
    ? initial
      ? `${initial}.`
      : ""
    : initial
      ? `${surname} ${initial}.`
      : surname;

  if (!nameLabel) {
    return kindLabel;
  }

  return kindLabel ? `${nameLabel} ${kindLabel}` : nameLabel;
};

const formatDateTime = (value) =>
  value ? moment(value).format("DD.MM.YYYY HH:mm") : "—";

const formatPersonName = (first_name, last_name) =>
  [first_name, last_name].filter(Boolean).join(" ").trim() || "—";

const buildCompanyDepartmentLine = (company, department_name) => {
  const parts = [company, department_name?.trim()].filter(Boolean);

  return parts.join(" - ");
};

const buildTileTooltip = (absence) => {
  const personName = formatPersonName(absence.first_name, absence.last_name);
  const approverName = formatPersonName(
    absence.approver_first_name,
    absence.approver_last_name
  );
  const company = getCompanyName(absence.company_id);

  return {
    title: personName,
    companyDepartment: buildCompanyDepartmentLine(
      company,
      absence.department_name
    ),
    createdAt: formatDateTime(absence.created_at),
    acceptedAt: absence.is_accepted
      ? formatDateTime(absence.accepted_at)
      : "—",
    approver: absence.is_accepted ? approverName : "—",
    isPending: !absence.is_accepted,
  };
};

const buildMonthGrid = (month) => {
  const monthStart = month.clone().startOf("month");
  const leadingEmptyCells = monthStart.isoWeekday() - 1;
  const cells = [];

  for (let index = 0; index < leadingEmptyCells; index += 1) {
    cells.push({ key: `empty-leading-${index}`, day: null });
  }

  for (let dayNumber = 1; dayNumber <= month.daysInMonth(); dayNumber += 1) {
    const day = month.clone().date(dayNumber);
    cells.push({ key: day.format("YYYY-MM-DD"), day });
  }

  return cells;
};

const MonthCalendar = ({ month, absences }) => {
  const cells = buildMonthGrid(month);
  const today = moment().startOf("day");
  const weekdays = getPolishWeekdayHeaders();

  return (
    <div className="absences-calendar__month">
      <h3 className="absences-calendar__month-title">
        {month.format("MMMM YYYY")}
      </h3>
      <div className="absences-calendar__grid-wrapper">
        <div className="absences-calendar__grid">
          <div className="absences-calendar__weekdays">
            {weekdays.map((weekday) => (
              <div key={weekday} className="absences-calendar__weekday">
                {weekday}
              </div>
            ))}
          </div>
          <div className="absences-calendar__days">
            {cells.map(({ key, day }) => {
              if (!day) {
                return (
                  <div
                    key={key}
                    className="absences-calendar__day absences-calendar__day--empty"
                  />
                );
              }

              const dayAbsences = getAbsencesForDay(day, absences);
              const isToday = day.isSame(today, "day");
              const weekend = isWeekend(day);

              return (
                <div
                  key={key}
                  className={[
                    "absences-calendar__day",
                    weekend && "absences-calendar__day--weekend",
                    isToday && "absences-calendar__day--today",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {!weekend && (
                    <>
                      <span className="absences-calendar__day-number">
                        {day.format("D")}
                      </span>
                      {dayAbsences.length > 0 && (
                        <div className="absences-calendar__tiles">
                          {dayAbsences.map((absence) => {
                            const tooltip = buildTileTooltip(absence);

                            return (
                              <span
                                key={absence.id}
                                className="absences-calendar__tile-wrapper"
                              >
                                <span
                                  className={[
                                    "absences-calendar__tile",
                                    !absence.is_accepted &&
                                      "absences-calendar__tile--pending",
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                  style={{
                                    backgroundColor: getDepartmentColor(absence),
                                  }}
                                >
                                  {formatTileLabel(absence)}
                                </span>
                                <span className="absences-calendar__tooltip">
                                  <strong>{tooltip.title}</strong>
                                  {tooltip.companyDepartment && (
                                    <span>{tooltip.companyDepartment}</span>
                                  )}
                                  <span>Dodano: {tooltip.createdAt}</span>
                                  <span>
                                    Zaakceptowano: {tooltip.acceptedAt}
                                  </span>
                                  <span>Przez: {tooltip.approver}</span>
                                  {tooltip.isPending && (
                                    <span className="absences-calendar__tooltip-pending">
                                      Oczekuje na akceptację
                                    </span>
                                  )}
                                </span>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthCalendar;
